import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ApiError } from '@shared/api/client';
import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { useCursorPages } from '@shared/pagination';
import { EmptyState } from '@shared/ui/EmptyState';
import { ErrorBanner } from '@shared/ui/ErrorBanner';
import { Pager } from '@shared/ui/pager';

import { copy } from './copy';
import { errors } from './errors';
import { InboxTable } from './Inbox.Table';
import { InboxToolbar } from './Inbox.Toolbar';
import { useDeleteNotifications, useMarkRead, useMarkUnread } from './mutations';
import { notificationsKey, notificationsRootKey, useNotifications } from './queries';
import { isUnread, type NotificationRow, type SortMode } from './types';

/**
 * The operator's own inbox. It renders what reached this account and does
 * nothing else: **no row routes anywhere, and no preference is set here.**
 *
 * Row activation navigating to a notification's subject is a deferred row, so
 * the message cell is a button that discloses the body in place rather than a
 * link; the per-event channel preference is an *account* preference and lives on
 * the subscriber host with the rest of the account surfaces. Neither absence is
 * an omission — each is a row read literally.
 *
 * Three pieces of state live here rather than in the URL, and each for its own
 * reason. The **cursor stack** is the keyset traversal: the service hands out
 * opaque cursors and the console keeps the ones it has walked through so
 * «السابق» has somewhere to go — a page number in the URL would be a figure
 * neither side can resolve back to a row. The **selection** is a gesture, not a
 * location, and survives no reload by design. The **open messages** are a
 * disclosure of rows this page happens to be holding.
 *
 * The sort, by contrast, is a view of the same list and would belong in the URL
 * the day the catalog gives this surface a shareable state; it does not, and
 * adding a parameter no row asks for would be inventing scope.
 */
export function InboxPage() {
  const [sort, setSort] = useState<SortMode>('date-desc');
  // The walk lives in the history entry and its ordinal in the URL, so Back and
  // Forward return to the exact page that was on screen — see the shared hook.
  const { cursor, page, hasPrevious, toPrevious, toNext, reset } = useCursorPages();
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [announcement, setAnnouncement] = useState('');

  const query = useNotifications(sort, cursor);
  const queryClient = useQueryClient();

  const markRead = useMarkRead();
  const markUnread = useMarkUnread();
  const remove = useDeleteNotifications();
  const busy = markRead.isPending || markUnread.isPending || remove.isPending;

  const items = useMemo(() => query.data?.items ?? [], [query.data]);

  /**
   * «Unread first» is a partition of the page, not a date order and not a
   * filter. The two date modes are the service's own sort keys and arrive
   * already ordered, so this touches nothing on either of them.
   */
  const rows = useMemo(() => orderRows(items, sort), [items, sort]);

  // The title this surface already carried copy for and never wrote. Every
  // other route in the console names itself in the tab; this one said only
  // «شاهدني · وحدة التحكّم», so a reader with several console tabs open could
  // not tell the inbox from anything else.
  useEffect(() => {
    document.title = copy.documentTitle;
  }, []);

  // A page that empties itself under a delete steps back rather than leaving the
  // operator on a blank table with a live «السابق» they have to find.
  useEffect(() => {
    if (!query.isPending && items.length === 0 && hasPrevious) toPrevious();
    // `toPrevious` is rebuilt on every render from the current history entry;
    // naming it would step back once per render instead of once per empty page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isPending, items.length, hasPrevious]);

  // A selection is scoped to the page it was made on: ids from a page that is no
  // longer rendered would be sent by a bulk action nobody aimed at them.
  useEffect(() => {
    setSelected((current) => {
      const visible = new Set(items.map((row) => row.id));
      const next = new Set([...current].filter((id) => visible.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [items]);

  const selectedIds = useMemo(() => [...selected], [selected]);

  function resetTraversal(nextSort: SortMode) {
    setSort(nextSort);
    reset();
    setSelected(new Set());
    setExpanded(new Set());
    setAnnouncement(copy.sort.announced);

    // **The order control has to act while the surface is in its error state**,
    // and that is exactly where it did nothing: the banner is drawn from the
    // last failure of *any* of the four queries here, so a stale mutation error
    // outlived the read it had nothing to do with, and a failed read stayed
    // cached under its own key while the previous page stayed on screen. Both
    // are cleared here, so a new selection always issues a new read and the
    // banner always describes the attempt in hand.
    markRead.reset();
    markUnread.reset();
    remove.reset();
    void queryClient.invalidateQueries({ queryKey: notificationsRootKey() });
  }

  function onToggleRow(id: string, checked: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function onToggleAll(checked: boolean) {
    setSelected(checked ? new Set(rows.map((row) => row.id)) : new Set());
  }

  function onOpenRow(row: NotificationRow) {
    const opening = !expanded.has(row.id);

    setExpanded((current) => {
      const next = new Set(current);
      if (opening) next.add(row.id);
      else next.delete(row.id);
      return next;
    });

    // Opening a message is reading it. This is the one read on the surface that
    // writes state, and it writes the only state a read can honestly write.
    if (opening && isUnread(row)) markRead.mutate([row.id]);
  }

  function onBulkMarkRead() {
    if (selectedIds.length === 0) return;
    markRead.mutate(selectedIds, {
      onSuccess: (result) => {
        setSelected(new Set());
        setAnnouncement(copy.bulk.markedRead(formatNumber(result.updated_count)));
      },
    });
  }

  function onBulkMarkUnread() {
    if (selectedIds.length === 0) return;
    markUnread.mutate(selectedIds, {
      onSuccess: (result) => {
        setSelected(new Set());
        setAnnouncement(copy.bulk.markedUnread(formatNumber(result.updated_count)));
      },
    });
  }

  function onBulkDelete() {
    if (selectedIds.length === 0) return;
    remove.mutate(selectedIds, {
      onSuccess: (result) => {
        setSelected(new Set());
        setExpanded(new Set());
        setAnnouncement(copy.bulk.deleted(formatNumber(result.deleted_count)));
      },
    });
  }

  const failure = query.error ?? markRead.error ?? markUnread.error ?? remove.error;

  function onRetry() {
    void queryClient.invalidateQueries({ queryKey: notificationsKey(sort, cursor) });
  }

  return (
    <>
      <Breadcrumb items={[{ label: copy.crumbs.root, to: '/' }, { label: copy.crumbs.current }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{copy.head.title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{copy.head.lede}</p>
      </div>

      {/*
        One polite region for the whole surface, not one per control. It carries
        the outcome of an action — a reorder, a mark, a delete — and never a
        counter: a value that changes every keystroke floods a screen reader.
      */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <section aria-labelledby="inbox-heading">
        <h2 id="inbox-heading" className="mb-4 font-display text-h2 font-bold">
          {copy.section.heading}
        </h2>

        {failure ? <InboxError error={failure} onRetry={onRetry} /> : null}

        {/*
          The order control and the three bulk actions are one strip, not two
          rows: both govern the table directly beneath them, and drawing them
          apart made the heading line, the actions, and the status line read as
          three unrelated fragments.
        */}
        <InboxToolbar
          sort={sort}
          onSortChange={resetTraversal}
          selectedCount={selected.size}
          busy={busy}
          onMarkRead={onBulkMarkRead}
          onMarkUnread={onBulkMarkUnread}
          onDelete={onBulkDelete}
        />

        {query.isPending ? (
          <p role="status" className="sr-only">
            {copy.states.loading}
          </p>
        ) : null}

        {!query.isPending && rows.length === 0 ? (
          <EmptyState message={hasPrevious ? copy.states.emptyPage : copy.states.empty} />
        ) : (
          <InboxTable
            rows={rows}
            pending={query.isPending}
            selected={selected}
            expanded={expanded}
            onToggleRow={onToggleRow}
            onToggleAll={onToggleAll}
            onOpenRow={onOpenRow}
          />
        )}

        {/*
          The keyset half of the shared control: no `pageCount`, no `total`, and
          therefore no ordinal strip. The owning service runs no count query for
          this list — that is the cost keyset paging exists to avoid — so the
          pager states the page it walked to and the rows it holds, and invents
          neither of the two figures it was not given.
        */}
        <Pager
          label={copy.pager.label}
          page={page}
          shown={rows.length}
          noun={{
            one: copy.pager.shownOne,
            two: copy.pager.shownTwo,
            few: copy.pager.shownFew,
            many: copy.pager.shownMany,
          }}
          hasPrevious={hasPrevious}
          hasNext={Boolean(query.data?.has_next)}
          onPrevious={() => {
            toPrevious();
            setSelected(new Set());
          }}
          onNext={() => {
            const next = query.data?.next_cursor;
            if (!next) return;
            toNext(next);
            setSelected(new Set());
          }}
        />
      </section>
    </>
  );
}

/**
 * The third sort mode, applied to the page in hand.
 *
 * It is a **stable** partition, so the service's date order survives inside each
 * half — «unread first» reorders the two groups and nothing within them.
 */
function orderRows(items: readonly NotificationRow[], sort: SortMode): NotificationRow[] {
  if (sort !== 'unread') return [...items];

  return [...items.filter(isUnread), ...items.filter((row) => !isUnread(row))];
}

function InboxError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, errors);

  return (
    <ErrorBanner
      message={resolved.message}
      tone={resolved.tone === 'danger' ? 'danger' : 'neutral'}
      onRetry={onRetry}
    />
  );
}
